import { useEffect, useCallback } from 'react';
import { useState } from 'react';
import {
  useGetExtendEstimates,
  useGetExtendChangeOrder,
  useGetQuoteCloseouts,
  useGetQuoteChangeOrderCloseouts,
  useUpdateChangeOrderExtensionMutation,
  useUpdateEstimateExtensionMutation,
  useUpdateQuoteChangeOrderCloseoutMutation,
  useUpdateQuoteCloseoutMutation,
} from '../../hooks/useCloseoutHooks';
import { formatNumber, formatCurrency } from './closeoutHelpers';
import { QuoteCloseout } from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';

/**
 * Custom hook to get extension data from either estimates or change orders
 * @returns Extension data from either source
 */
export const useExtensionData = () => {
  // Only call hooks if we have the required IDs
  const estimateQuery = useGetExtendEstimates();
  const changeOrderQuery = useGetExtendChangeOrder();

  // Handle errors gracefully - if a query fails due to missing ID, treat it as no data
  const estimateExtension = estimateQuery.error ? null : estimateQuery.data;
  const changeOrderExtension = changeOrderQuery.error ? null : changeOrderQuery.data;

  const isEstimateLoading = estimateQuery.isLoading && !estimateQuery.error;
  const isChangeOrderLoading = changeOrderQuery.isLoading && !changeOrderQuery.error;

  // Return whichever data is available (prioritizing change order data)
  return {
    extensionData: changeOrderExtension || estimateExtension,
    refetchExtensionData: async () => {
      // Only refetch queries that have data or are not in error state
      const results = [];

      if (estimateExtension && !estimateQuery.error) {
        try {
          const result = await estimateQuery.refetch();
          results.push({ source: 'estimate', result });
        } catch (error) {
          console.warn('Failed to refetch estimate data:', error);
        }
      }

      if (changeOrderExtension && !changeOrderQuery.error) {
        try {
          const result = await changeOrderQuery.refetch();
          results.push({ source: 'changeOrder', result });
        } catch (error) {
          console.warn('Failed to refetch change order data:', error);
        }
      }

      // If no queries were refetched, try to refetch the estimate query as fallback
      if (results.length === 0 && !estimateQuery.error) {
        console.log('No data to refetch, trying estimate query as fallback');
        try {
          const result = await estimateQuery.refetch();
          results.push({ source: 'estimate', result });
        } catch (error) {
          console.warn('Failed to refetch estimate data as fallback:', error);
        }
      }

      // Return the first successful result or the estimate result
      const successfulResult = results.find(r => r.result.isSuccess) || results[0];
      return successfulResult?.result || { isSuccess: false, data: null };
    },
    isLoading: isEstimateLoading || isChangeOrderLoading,
    hasEstimateData: !!estimateExtension,
    hasChangeOrderData: !!changeOrderExtension,
    estimateError: estimateQuery.error,
    changeOrderError: changeOrderQuery.error,
  };
};

/**
 * Custom hook to get closeout data from either estimates or change orders
 * @returns Closeout data from either source
 */
export const useCloseoutData = () => {
  const { data: estimateCloseout, refetch: refetchEstimateCloseout } = useGetQuoteCloseouts();
  const { data: changeOrderCloseout, refetch: refetchChangeOrderCloseout } =
    useGetQuoteChangeOrderCloseouts();

  // Return whichever data is available (prioritizing change order data)
  return {
    closeoutData: changeOrderCloseout || estimateCloseout,
    refetchCloseoutData: refetchChangeOrderCloseout || refetchEstimateCloseout,
  };
};

/**
 * Custom hook to update extension data for either estimates or change orders
 * @returns Mutations for updating extension data
 */
export const useUpdateExtensionData = () => {
  const { mutateAsync: updateExtendEstimate } = useUpdateEstimateExtensionMutation();
  const { mutateAsync: updateExtendChangeOrder } = useUpdateChangeOrderExtensionMutation();

  return {
    updateExtension: updateExtendChangeOrder || updateExtendEstimate,
  };
};

/**
 * Custom hook to update closeout data for either estimates or change orders
 * @returns Mutations for updating closeout data
 */
export const useUpdateCloseoutData = () => {
  const { mutateAsync: updateCloseoutEstimate } = useUpdateQuoteCloseoutMutation();
  const { mutateAsync: updateCloseoutChangeOrder } = useUpdateQuoteChangeOrderCloseoutMutation();

  return {
    updateCloseout: updateCloseoutChangeOrder || updateCloseoutEstimate,
  };
};

/**
 * Custom hook to manage edited values state and related operations for QuoteExtention
 */
export const useInitializeEditedValues = () => {
  const { extensionData } = useExtensionData();
  const { closeoutData, refetchCloseoutData } = useCloseoutData();
  const { updateExtension } = useUpdateExtensionData();
  const { updateCloseout } = useUpdateCloseoutData();

  const [editedValues, setEditedValues] = useState<Record<string, Record<string, string>>>({});
  const [quotedCost, setQuotedCost] = useState<string>('0.00');

  // Initialize quoted cost from closeout data
  useEffect(() => {
    if (closeoutData?.closeoutsList?.[0]) {
      setQuotedCost(formatCurrency(closeoutData.closeoutsList[0].quotedcost || 0));

      // Initialize the default adjusted percent for all items
      const defaultAdjustedPercent = formatNumber(
        closeoutData.closeoutsList[0].adjustedpercent || 0
      );

      // Update each extension with the default adjusted percent if it doesn't exist yet
      if (extensionData?.extendestimatesList?.length) {
        setEditedValues(prev => {
          const updatedValues = { ...prev };
          extensionData.extendestimatesList.forEach(extension => {
            updatedValues[extension.extensionid] = {
              ...(updatedValues[extension.extensionid] || {}),
              adjustedPercent:
                updatedValues[extension.extensionid]?.adjustedPercent || defaultAdjustedPercent,
            };
          });
          return updatedValues;
        });
      }
    }
  }, [closeoutData, extensionData]);

  // Initialize edited values when extension data changes
  useEffect(() => {
    if (extensionData?.extendestimatesList?.length) {
      const initialValues: Record<string, Record<string, string>> = {};
      extensionData.extendestimatesList.forEach(extension => {
        initialValues[extension.extensionid] = {
          overrideitemunitcost: formatNumber(extension.overrideitemunitcost || 0),
          adjustedPercent:
            editedValues[extension.extensionid]?.adjustedPercent ||
            (closeoutData?.closeoutsList?.[0]
              ? formatNumber(closeoutData.closeoutsList[0].adjustedpercent || 0)
              : '0.00'),
        };
      });
      setEditedValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionData, closeoutData]);

  // Function to handle extension item cost changes
  const handleExtensionCostChange = useCallback((extensionId: string, value: string) => {
    setEditedValues(prev => ({
      ...prev,
      [extensionId]: {
        ...prev[extensionId],
        overrideitemunitcost: value,
      },
    }));
  }, []);

  // Function to handle adjusted percent changes for each extension
  const handleAdjustedPercentChange = useCallback((extensionId: string, value: string) => {
    setEditedValues(prev => ({
      ...prev,
      [extensionId]: {
        ...prev[extensionId],
        adjustedPercent: value,
      },
    }));
  }, []);

  // Handle field changes for closeout data
  const handleFieldChange = useCallback(
    async (
      field: keyof Pick<QuoteCloseout.AsObject, 'quotedcost' | 'adjustedpercent'>,
      value: string,
      extensionId: string
    ) => {
      // Allow only numbers with up to 2 decimals.
      const regex = /^[0-9]*\.?[0-9]{0,2}$/;
      if (!regex.test(value) || Number(value) > 99999999.99) return;

      // Only proceed if closeout data exists
      if (!closeoutData?.closeoutsList[0]) return;

      // Update local state first
      if (field === 'adjustedpercent') {
        setEditedValues(prev => ({
          ...prev,
          [extensionId]: {
            ...prev[extensionId],
            adjustedPercent: value,
          },
        }));
      }

      const closeoutItem = { ...closeoutData.closeoutsList[0] };

      // Update the specific field with the new value
      if (field === 'quotedcost') {
        closeoutItem.quotedcost = Number(value);
      } else if (field === 'adjustedpercent') {
        closeoutItem.adjustedpercent = Number(value);
      }

      // Ensure required string fields aren't undefined
      if (closeoutItem.closeoutid === undefined) closeoutItem.closeoutid = '';
      if (closeoutItem.estimateid === undefined) closeoutItem.estimateid = '';
      if (closeoutItem.closeoutdefaultquoteid === undefined)
        closeoutItem.closeoutdefaultquoteid = '';

      await updateCloseout(closeoutItem, {
        onSuccess: () => {
          refetchCloseoutData();
        },
      });
    },
    [closeoutData, updateCloseout, refetchCloseoutData]
  );

  // Function to update extension in the database
  const updateExtensionCost = useCallback(
    async (extension: any, newValue: string) => {
      // Allow only numbers with up to 2 decimals
      const regex = /^[0-9]*\.?[0-9]{0,2}$/;
      if (!regex.test(newValue) || Number(newValue) > 99999999.99) return;

      try {
        // Create the update request with the proper shape
        const updatedExtension = {
          extendestimatesList: [
            {
              ...extension,
              overrideitemunitcost: Number(newValue),
              isoverrideitemunitcost: true,
            },
          ],
          sessiontoken: '', // The session token will be added by the API service
        };

        await updateExtension(updatedExtension, {
          onSuccess: () => {
            console.log('Extension updated successfully');
          },
          onError: error => {
            console.error('Failed to update extension:', error);
          },
        });
      } catch (error) {
        console.error('Error updating extension:', error);
      }
    },
    [updateExtension]
  );

  return {
    editedValues,
    quotedCost,
    handleExtensionCostChange,
    handleAdjustedPercentChange,
    handleFieldChange,
    updateExtensionCost,
  };
};
